import React, { useState } from 'react';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';

// Components
import Loader from '../../../General/Loader';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';

// Icons
import FileIcon from '../../../../assets/images/admin/file.svg';
import SaveAlt from '@mui/icons-material/SaveAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Styles
import './styles.scss';

const QueryDetails = (props) => {
  const { open, onClose, data, loading } = props;
  const [isDownloading, setIsDownloading] = useState({
    status: false,
    id: null,
  });
  const dispatch = useDispatch();

  const getFileName = (fileUrl = '') => fileUrl.split('/').pop() || 'File';

  const getFileExtension = (filename = '') => getFileName(filename).split('.').pop() || 'FILE';

  const revisions = (data?.activityComments || [])
    .filter((activityComment) => {
      const revisionNumber = Number(activityComment?.revisionNumber);

      return activityComment?.action !== 'created' && Number.isFinite(revisionNumber) && revisionNumber > 0;
    })
    .sort((firstRevision, secondRevision) => Number(secondRevision.revisionNumber) - Number(firstRevision.revisionNumber));

  const downloadFile = async (fileUrl) => {
    if (!fileUrl) {
      dispatch(failure(MESSAGES.ADMIN_DASHBOARD.QUERIES.URL_NOT_FOUND));
      return;
    }

    try {
      setIsDownloading({
        status: true,
        id: fileUrl,
      });
      const response = await fetch(fileUrl, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob(); // Get the file as a blob
      const blobUrl = window.URL.createObjectURL(blob); // Create a URL for the blob
      const link = document.createElement('a');

      link.href = blobUrl;
      link.download = fileUrl.split('/').pop(); // Extract file name from URL
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup the blob URL
      window.URL.revokeObjectURL(blobUrl);
      dispatch(success(MESSAGES.ADMIN_DASHBOARD.QUERIES.FILE_DOWNLOADED));
    } catch (error) {
      dispatch(failure(MESSAGES.ADMIN_DASHBOARD.QUERIES.FILE_DOWNLOAD_FAILED));
    } finally {
      setIsDownloading({
        status: false,
        id: null,
      });
    }
  };

  const renderFiles = (files = []) => (
    files?.length > 0 ? (
      <Box className="filesWrapper">
        {files.map((file, index) => {
          return (
            <Box className="fileName" key={`${file}-${index}`}>
              <Box className="fileBox">
                <img src={FileIcon} alt="file" />
                <Typography>{getFileExtension(file)}</Typography>
              </Box>
              <Typography className="name">
                {getFileName(file)}
              </Typography>
              <Box className="downloadIconWrapper">
                {isDownloading.status && isDownloading.id === file ? (
                  <CircularProgress
                    sx={{
                      color: '#ed5c2f',
                      width: '20px !important',
                      height: '20px !important',
                    }}
                  />
                ) : (
                  <SaveAlt onClick={() => downloadFile(file)} />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    ) : (
      <>
        <Typography className="noFileUploaded">No files uploaded</Typography>
      </>
    )
  );

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={data?.meta?.templateTitle || 'Query details'}
      className="olcAdminFormModal queryDetailsWrapper"
    >
      <Box className="queryDetailsWrapper">
        {loading ? (
          <Box className="loaderWrapper">
            <Loader />
          </Box>
        ) : (
          <>
            <Box className="queryDetails">
              <Box className="infoRow">
                <Typography>URL</Typography>
                {data?.url ? (
                  <Typography className="urlLink" onClick={() => window.open(data?.url, '_blank')}>
                    {data?.url}
                  </Typography>
                ) : (
                  <Typography>-</Typography>
                )}
              </Box>
              <Box className="commentsDiv">
                <Typography>Comments</Typography>
                <Box className="commentsWrapper">
                  <Box className="commentBox">
                    <Typography className="content">
                      {data?.comments ? data?.comments : '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="filesDiv">
                <Typography>Files</Typography>
                {renderFiles(data?.files)}
              </Box>
              {revisions.length > 0 && (
                <Box className="revisionsDiv">
                  <Typography>Revisions</Typography>
                  <Box className="revisionsWrapper">
                    {revisions.map((revision) => (
                      <Accordion className="revisionAccordion" key={revision.id || revision.revisionNumber}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography>{`Revision ${revision.revisionNumber}`}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box className="commentsDiv revisionCommentsDiv">
                            <Typography>Comments</Typography>
                            <Box className="commentsWrapper">
                              <Box className="commentBox">
                                <Typography className="content">
                                  {revision?.comment ? revision.comment : '-'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Box className="filesDiv revisionFilesDiv">
                            <Typography>Files</Typography>
                            {renderFiles(revision?.files)}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </OlcFormModal>
  );
};

export default QueryDetails;
